import { clsx } from 'clsx';
import { type KeyboardEvent, type ReactNode } from 'react';
import styles from './SourceInfoTile.module.scss';

export interface SourceInfoTileField {
    label: string;
    value: string;
}

export interface SourceInfoTileProps {
    title: string;
    fields: SourceInfoTileField[];
    selected?: boolean;
    onSelect?: () => void;
    className?: string;
}

function TileField({ label, value }: SourceInfoTileField): ReactNode {
    return (
        <p className={`${styles.field} text-xs text-secondary m-0 pb-0`}>
            <span className="font-bold">{label}: </span>
            <span>{value}</span>
        </p>
    );
}

export function SourceInfoTile({
    title,
    fields,
    selected = false,
    onSelect,
    className,
}: SourceInfoTileProps): ReactNode {
    const selectable = Boolean(onSelect);
    const tileClasses = clsx(
        'flex flex-column gap-2 p-3 border-round-lg bg-n10',
        styles.tile,
        { [styles.selected]: selected, 'cursor-pointer': selectable },
        className,
    );

    function handleKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        onSelect?.();
    }

    return (
        <div
            role={selectable ? 'button' : undefined}
            tabIndex={selectable ? 0 : undefined}
            className={tileClasses}
            onClick={onSelect}
            onKeyDown={selectable ? handleKeyDown : undefined}
        >
            <h4 className="m-0">{title}</h4>
            {fields.map((field) => (
                <TileField
                    key={field.label}
                    label={field.label}
                    value={field.value}
                />
            ))}
        </div>
    );
}
